import { useState, useEffect } from "react";
import DashboardHeader from "../../components/DashboardHeader";
import Sidebar from "../../components/Sidebar";
import "./InvoicesAccountant.css";
import { FaSave, FaUpload, FaTimes, FaEye } from "react-icons/fa";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  createNewInvoiceApi,
  takeImageUploadSalesApi,
  getSalesListApi,
} from "../../services/apiCall";
import Pagination from "../../components/Pagination";
import CommonLoader from "../../components/CommonLoader";

const InvoicesAccountant = () => {
  const [showModal, setShowModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [grossAmount, setGrossAmount] = useState("");
  const [netAmount, setNetAmount] = useState("");
  const [vat, setVat] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [businessName, setBusinessName] = useState("Premichly Pvt Ltd");

  // Pagination
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const userId = useSelector((state) => state?.user?.userId);
  const businessId = useSelector((state) => state?.user?.businessId);

  useEffect(() => {
    if (businessId) fetchInvoices();
  }, [businessId]);

  const fetchInvoices = async () => {
    try {
      setIsLoading(true);
      const res = await getSalesListApi({ businessId });
      console.log("res>>>>>>>", res?.data);
      setInvoices(res?.data?.sales || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load invoices");
    } finally {
      setIsLoading(false);
    }
  };

  const totalPages = Math.ceil(invoices.length / itemsPerPage);
  const displayedInvoices = invoices.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const handleSave = async () => {
    console.log("invoiceNumber.......");
    if (!invoiceNumber || !grossAmount || !netAmount || !vat) {
      toast.error("⚠️ Please fill all required fields.");
      return;
    }

    const gross = parseFloat(grossAmount);
    if (isNaN(gross) || gross <= 0) {
      toast.error("Gross amount must be a positive number.");
      return;
    }

    const payload = {
      businessId,
      createdBy: userId,
      invoiceNumber,
      gross: parseFloat(grossAmount),
      net: parseFloat(netAmount),
      vat: parseFloat(vat),
    };
    console.log("payload", payload);

    try {
      setIsLoading(true);
      const response = await createNewInvoiceApi(payload);
      console.log("Response Data:", response);
      if (response?.data) {
        toast.success(
          `Invoice #${
            response.data.expense?.invoiceNumber || ""
          } created successfully!`,
        );
        setInvoiceNumber("");
        setGrossAmount("");
        setNetAmount("");
        setVat("");
        setImagePreview(null);
        setImage(null);
      } else {
        toast.error("Unexpected server response.");
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong. Try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    setImage(file);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("businessId", businessId);
    formData.append("type", "sale");

    try {
      const response = await takeImageUploadSalesApi(formData);
      const data = response?.data?.data;
      console.log("Upload Response Data:", response);

      if (data) {
        if (data.gross) setGrossAmount(data.gross.toString());
        if (data.vat) setVat(data.vat.toString());
        if (data.net) setNetAmount(data.net.toString());

        if (data.documentId) {
          console.log("Uploaded file path:", data.documentId);
        }

        toast.success("Receipt uploaded & data extracted!");
      }
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error);
      toast.error("Failed to upload image.");
    }
  };

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="app">
      <Sidebar />
      <CommonLoader visible={isLoading} />
      <main className="main">
        <DashboardHeader title="Invoices" subtitle="Invoices for the company" />
        <div className="invoice-card">
          <div className="invoice-card-header">
            <h2>Invoices</h2>
            <button
              className="add-btn interactive-pill"
              onClick={() => setShowModal(true)}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Invoice
            </button>
          </div>

          <div className="invoice-table">
            <div className="invoice-table-header">
              <div>Date</div>
              <div>Client</div>
              <div>Invoice No.</div>
              <div>Gross (€)</div>
              <div>VAT (€)</div>
              <div>Net (€)</div>
              <div className="text-right">Actions</div>
            </div>
            {displayedInvoices.length === 0 ? (
              <div className="invoice-table-empty">
                <p>No invoices found.</p>
              </div>
            ) : (
              displayedInvoices.map((invoice) => {
                const formattedDate = invoice.createdAt
                  ? new Date(invoice.createdAt).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "N/A";

                return (
                  <div key={invoice.id} className="invoice-table-row">
                    <div style={{ fontWeight: 500, color: "#374151" }}>
                      {formattedDate}
                    </div>
                    <div style={{ color: "#6b7280" }}>
                      {invoice.client || "N/A"}
                    </div>
                    <div style={{ fontWeight: 600 }}>
                      {invoice.invoiceNumber || "N/A"}
                    </div>
                    <div>{invoice.gross || "0.00"}</div>
                    <div>{invoice.vat || "0.00"}</div>
                    <div style={{ fontWeight: 600 }}>
                      {invoice.net || "0.00"}
                    </div>
                    <div className="text-right">
                      <button
                        className="action-btn interactive-pill"
                        style={{
                          padding: "8px 12px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#f3f4f6",
                          color: "#4f46e5",
                          border: "none",
                        }}
                      >
                        <FaEye />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ padding: "0 24px 24px 24px" }}>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </main>

      {/* MODAL */}
      {showModal && (
        <div
          className="modal-backdrops"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
            }
          }}
        >
          <div className="modal-container">
            <div className="modal-header">
              <h2>Add New Invoice</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="invoice-form-grid">
              <div className="form-group full">
                <label>Invoice Number</label>
                <input
                  type="text"
                  placeholder="INV-2025-001"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                />
              </div>

              <div className="form-group full">
                <label>Invoice Date</label>
                <input
                  type="date"
                  // you can add defaultValue={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Amount section - better visual grouping */}
              <div className="amounts-group">
                <div className="form-group">
                  <label>
                    Net Amount <span className="currency">€</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={netAmount}
                    onChange={(e) => setNetAmount(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>
                    VAT <span className="currency">€</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={vat}
                    onChange={(e) => setVat(e.target.value)}
                  />
                </div>

                <div className="form-group total">
                  <label>
                    Gross Amount <span className="currency">€</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={grossAmount}
                    readOnly
                    className="total-input"
                  />
                </div>
              </div>

              <div className="form-group full">
                <label>Attach Receipt / Invoice Scan</label>
                <div className="file-upload-area">
                  <input
                    id="invoiceFile"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleImageChange}
                    hidden
                  />
                  <label htmlFor="invoiceFile" className="file-upload-label">
                    <FaUpload className="upload-icon" />
                    <div className="upload-text">
                      <strong>Click to upload</strong> or drag & drop
                      <span>PNG, JPG, PDF (max. 10MB)</span>
                    </div>
                  </label>
                </div>

                {imagePreview && (
                  <div className="image-preview-container">
                    <img src={imagePreview} alt="Receipt preview" />
                    <button
                      type="button"
                      className="remove-preview-btn"
                      onClick={() => {
                        setImage(null);
                        setImagePreview(null);
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? "Creating..." : "Create Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesAccountant;
